import { useEffect, useState } from 'react';
import styled from 'foundations-theming-specialty/utils';
import unescapedText from 'I18n/utils/unescapedText';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { HR } from 'foundations-components/transitional/components/HR';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { SidebarLeftCollapseIcon } from 'foundations-assets/icons/components/SidebarLeftCollapseIcon';
import { SidebarLeftExpandIcon } from 'foundations-assets/icons/components/SidebarLeftExpandIcon';
import { usePortalNavigation } from '../../hooks/usePortalNavigation';
import { useCreatableSpaces } from '../../hooks/useCreatableSpaces';
import { useIsHubSpotUser } from '../../hooks/useIsHubSpotUser';
import { useIsUngated } from '../../hooks/useIsUngated';
import { useIsPillBorderRadius } from '../../hooks/useIsPillBorderRadius';
import { CREATE_SPACE_GATE } from '../../constants/gates';
import { hasHsRouteKeyCookie } from '../../utils/hsRouteKey';
import { PortalsSideNavItem } from './PortalsSideNavItem';
import { SiteMenuNavItem } from './SiteMenuNavItem';
import { CreateSpaceNavItem } from './CreateSpaceNavItem';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HsRouteKeyTape = styled.div.withConfig({
  displayName: "PortalsSideNav__HsRouteKeyTape"
})({
  position: 'absolute',
  insetBlockEnd: '0px',
  inlineSize: '56px',
  blockSize: '12px',
  borderRadius: '0px',
  backgroundImage: 'repeating-linear-gradient(-45deg, #FCC200 0 6px, #1A1A1A 6px 12px)',
  cursor: 'help'
});

// Scoped styled button so the toggle matches the nav item icons exactly (same
// 40px box, border-radius token and 20px glyph) — the Trellis IconButton locks
// its glyph to 16px and applies its own radius. `user-select: none` stops rapid
// (double) clicks from starting a text selection that bleeds into the content.
const ToggleButton = styled.div.withConfig({
  displayName: "PortalsSideNav__ToggleButton"
})(({
  theme,
  $isPill
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  inlineSize: '40px',
  blockSize: '40px',
  flexShrink: 0,
  borderRadius: theme.borderRadius[$isPill ? '0' : '300'],
  userSelect: 'none',
  transition: 'background-color 150ms',
  '&:hover, &[data-vr-state="hover"]': {
    backgroundColor: theme.color['fill-surface-default-hover']
  }
}));

// Duration of the label opacity fade. Kept in step with the rail's width
// transition in PortalsAppLayout so the text finishes fading exactly as the
// rail finishes sliding — and, on collapse, so the labels stay mounted until
// the rail is closed before they unmount.
const LABEL_FADE_MS = 200;

// `nav` root as a styled element so we can (a) pin the layout width while the
// rail animates (see `expandedInlineSize`) and (b) fade every inline label in
// one place. Each label element opts in with `data-nav-label`, so entry points,
// the site menu, and the create-space action all fade together.
const NavRoot = styled.nav.withConfig({
  displayName: "PortalsSideNav__NavRoot"
})(({
  $inlineSize,
  $labelsVisible
}) => ({
  blockSize: '100%',
  inlineSize: $inlineSize,
  '& [data-nav-label]': {
    transition: `opacity ${LABEL_FADE_MS}ms ease`,
    opacity: $labelsVisible ? 1 : 0
  }
}));

// Drives the expand/collapse label fade. `mounted` keeps the expanded layout
// (inline labels, pinned width) alive; `visible` toggles their opacity.
//
// Expand: mount on the next frame with `visible` still false (opacity 0), then
// flip `visible` a frame later so opacity transitions 0 -> 1 as the rail slides
// open. Collapse: drop `visible` (fade out) but keep `mounted` for the fade
// duration so the labels are still present while the rail slides closed, then
// unmount them. Every state change is deferred to a rAF/timeout so none runs
// synchronously inside the effect (which would cascade an extra render).
function useLabelFade(isExpanded) {
  const [mounted, setMounted] = useState(isExpanded);
  const [visible, setVisible] = useState(isExpanded);
  useEffect(() => {
    if (isExpanded) {
      let fadeInFrame = 0;
      const mountFrame = requestAnimationFrame(() => {
        setMounted(true);
        fadeInFrame = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(mountFrame);
        cancelAnimationFrame(fadeInFrame);
      };
    }
    const fadeOutFrame = requestAnimationFrame(() => setVisible(false));
    const unmountTimer = setTimeout(() => setMounted(false), LABEL_FADE_MS);
    return () => {
      cancelAnimationFrame(fadeOutFrame);
      clearTimeout(unmountTimer);
    };
  }, [isExpanded]);
  return {
    mounted,
    visible
  };
}
export function PortalsSideNav({
  history,
  currentPathname,
  isExpanded = false,
  onToggleExpanded,
  expandedInlineSize
}) {
  var _siteMenuData$items$l;
  const {
    navItems,
    siteMenuData
  } = usePortalNavigation(currentPathname);
  const isHubSpotUser = useIsHubSpotUser();
  const isCreateSpaceGateEnabled = useIsUngated(CREATE_SPACE_GATE);
  // Creating a space is a gated, HubSpot-user affordance, so skip the RPC
  // entirely for the majority of visitors who can never create one. The backend
  // also self-gates to HubSpot users, but skipping avoids a wasted call and the
  // identity gate keeps the action from leaking to a plain membership contact.
  const canCreateSpace = isCreateSpaceGateEnabled && isHubSpotUser;
  const {
    creatableSpaces
  } = useCreatableSpaces({
    skip: !canCreateSpace
  });
  const isPill = useIsPillBorderRadius();
  const showCreateSpace = canCreateSpace && creatableSpaces.length > 0;
  const showHsRouteKeyTape = hasHsRouteKeyCookie();
  const toggleLabel = unescapedText(isExpanded ? 'app.sideNav.collapse' : 'app.sideNav.expand');

  // `mounted` keeps the expanded layout (inline labels + pinned width) alive
  // through the collapse fade; `visible` drives the label opacity. The toggle
  // button below still tracks the immediate `isExpanded` so its icon flips as
  // soon as it is clicked.
  const {
    mounted,
    visible
  } = useLabelFade(isExpanded);
  // Still rendered expanded, but the rail is closing: labels fade out as an
  // overlay and rows return to icon height immediately (see PortalsSideNavItem).
  // Gated on `expandedInlineSize` because the overlay only makes sense paired
  // with the pinned-width rail animation — a consumer that renders this nav
  // without a width-animating container should never get overlay-positioned
  // labels.
  const isCollapsing = Boolean(expandedInlineSize) && mounted && !isExpanded;
  return /*#__PURE__*/_jsxs(NavRoot, {
    "aria-label": "Portal navigation",
    $inlineSize: mounted && expandedInlineSize ? expandedInlineSize : '100%',
    $labelsVisible: visible,
    children: [showHsRouteKeyTape && /*#__PURE__*/_jsx(HsRouteKeyTape, {
      title: "A preview route key is enabled",
      "aria-label": "A preview route key is enabled",
      "data-test-id": "portals-side-nav-hs-route-key-tape"
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      blockSize: "100%",
      gap: "400",
      paddingInline: "200",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "stretch",
        gap: "400",
        children: [navItems.map(item => /*#__PURE__*/_jsx(PortalsSideNavItem, {
          item: item,
          history: history,
          isExpanded: mounted,
          isCollapsing: isCollapsing
        }, item.id)), onToggleExpanded && navItems.length > 0 && (((_siteMenuData$items$l = siteMenuData === null || siteMenuData === void 0 ? void 0 : siteMenuData.items.length) !== null && _siteMenuData$items$l !== void 0 ? _siteMenuData$items$l : 0) > 0 || showCreateSpace) && /*#__PURE__*/_jsx(View, {
          paddingInline: "100",
          children: /*#__PURE__*/_jsx(HR, {
            distance: "flush"
          })
        }), siteMenuData && siteMenuData.items.length > 0 && /*#__PURE__*/_jsx(SiteMenuNavItem, {
          menuData: siteMenuData,
          isExpanded: mounted
        }), showCreateSpace && /*#__PURE__*/_jsx(CreateSpaceNavItem, {
          creatableSpaces: creatableSpaces,
          history: history,
          isExpanded: mounted
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flexGrow: "1"
      }), onToggleExpanded && /*#__PURE__*/_jsx(Flex, {
        alignItems: "start",
        children: /*#__PURE__*/_jsx(Clickable, {
          onClick: onToggleExpanded,
          "aria-label": toggleLabel,
          "aria-expanded": isExpanded,
          "data-test-id": "portals-side-nav-toggle",
          children: /*#__PURE__*/_jsx(ToggleButton, {
            $isPill: isPill,
            children: isExpanded ? /*#__PURE__*/_jsx(SidebarLeftCollapseIcon, {
              color: "text-core-default",
              size: "500"
            }) : /*#__PURE__*/_jsx(SidebarLeftExpandIcon, {
              color: "text-core-default",
              size: "500"
            })
          })
        })
      })]
    })]
  });
}